// Connexion au NOUVEAU système (comptes d'autopilot-api, code de sécurité d'autopilot-back).
// Inactif tant que REACT_APP_AUTOPILOT_AUTH vaut autre chose que « 1 » : les pages de connexion gardent le Classique
// jusqu'à la bascule. Parcours : code envoyé par e-mail → preuve d'adresse → session du nouveau système.
import axios from 'axios';

export const AUTOPILOT_TOKEN_KEY = 'autopilot_token';

const trim = (v?: string) => (v || '').trim().replace(/\/+$/, '');
const apiUrl = () => trim(process.env.REACT_APP_AUTOPILOT_API_URL);
const backUrl = () => trim(process.env.REACT_APP_AUTOPILOT_BACK_URL);

const OPTIN_KEY = 'autopilot_auth_optin';
const OPTIN_PARAM = 'nouveau-systeme';

/** Mode « optin » : un navigateur s'inscrit au nouveau système avec ?nouveau-systeme=1 (et en sort avec =0) */
const optedIn = (): boolean => {
    try {
        const v = new URLSearchParams(window.location.search).get(OPTIN_PARAM);
        if (v === '1') localStorage.setItem(OPTIN_KEY, '1');
        if (v === '0') localStorage.removeItem(OPTIN_KEY);
        return localStorage.getItem(OPTIN_KEY) === '1';
    } catch {
        return false;
    }
};

/** Nouvelle connexion active et branchée (autopilot-api et autopilot-back renseignés).
 *  REACT_APP_AUTOPILOT_AUTH : « 1 » pour tout le monde, « optin » pour les navigateurs inscrits (tests d'équipe en
 *  production), autre valeur : Classique. */
export const isAutopilotAuth = (): boolean => {
    if (apiUrl() === '' || backUrl() === '') return false;
    const mode = (process.env.REACT_APP_AUTOPILOT_AUTH || '').trim();
    return mode === '1' || (mode === 'optin' && optedIn());
};

/** Inscription du navigateur au mode « optin » (bandeau « revenir au Classique ») */
export const isOptinMode = (): boolean => (process.env.REACT_APP_AUTOPILOT_AUTH || '').trim() === 'optin';
export const leaveOptin = (): void => {
    try { localStorage.removeItem(OPTIN_KEY); } catch { /* stockage indisponible */ }
};

const origin = () => ({ 'X-App-Origin': process.env.REACT_APP_ORIGIN_NAME || '', 'Content-Type': 'application/json' });

/** Message lisible : autopilot-api place le texte dans `detail`, autopilot-back (NestJS) dans `message`. */
export const authErrorMessage = (error: any, fallback: string): string => {
    const data = error?.response?.data;
    const text = data?.detail ?? data?.message;
    if (typeof text === 'string' && text.trim()) return text;
    if (Array.isArray(text) && text.length) return text.map((t: any) => (typeof t === 'string' ? t : t?.msg)).join('\n');
    if (error?.request && !error?.response) return 'Serveur injoignable.';
    return fallback;
};

/** « connecte » : jeton de session ; « en_attente » : un admin doit valider l'accès (message à afficher) ;
 *  « code_requis » : mot de passe ou Google juste, la session n'est donnée qu'avec le code (ticket) ;
 *  « entreprise_requise » : première connexion, le nom de l'entreprise est demandé (ticket) avant la session. */
export interface AutopilotSession {
    status: 'connecte' | 'en_attente' | 'code_requis' | 'entreprise_requise';
    ticket?: string;
    email?: string;
    first_name?: string;
    token?: string;
    user?: { id: string; email: string; first_name?: string; last_name?: string };
    company?: { id: string; name: string; role: string } | string;
    message?: string;
}

/** Envoie le code de sécurité à 6 chiffres (une demande par minute). */
export const sendSecurityCode = async (email: string, firstName = ''): Promise<void> => {
    await axios.post(`${backUrl()}/auth/code`, firstName ? { email, first_name: firstName } : { email },
        { headers: { 'Content-Type': 'application/json', 'x-api-key': process.env.REACT_APP_AUTOPILOT_BACK_KEY || '' }, timeout: 30000 });
};

/** Vérifie le code : renvoie la preuve d'adresse (valable 15 minutes) attendue par autopilot-api. */
export const verifySecurityCode = async (email: string, code: string): Promise<string> => {
    const { data } = await axios.post(`${backUrl()}/auth/code/verify`, { email, code },
        { headers: { 'Content-Type': 'application/json', 'x-api-key': process.env.REACT_APP_AUTOPILOT_BACK_KEY || '' }, timeout: 30000 });
    return data.email_proof;
};

const post = async (path: string, body: any, token?: string): Promise<any> => {
    const headers: Record<string, string> = origin();
    if (token) headers.Authorization = `Bearer ${token}`;
    const { data } = await axios.post(`${apiUrl()}${path}`, body, { headers, timeout: 30000 });
    return data;
};

// Les connexions renvoient la session sans la garder : elle est ouverte (jeton stocké) par features/accounts/session.ts,
// après le code de sécurité.
/** Connexion (ou inscription) avec la preuve d'adresse ; accès en attente tant qu'un admin n'a pas validé. */
export const loginWithProof = async (emailProof: string, profile: { first_name?: string; last_name?: string; company_name?: string } = {}) =>
    (await post('/autopilot/auth/login-code', { email_proof: emailProof, ...profile })) as AutopilotSession;

export const registerWithProof = async (emailProof: string, password: string,
    profile: { first_name?: string; last_name?: string; company_name?: string } = {}) =>
    (await post('/autopilot/auth/register', { email_proof: emailProof, password, ...profile })) as AutopilotSession;

export const loginWithPassword = async (email: string, password: string) =>
    (await post('/autopilot/auth/login', { email, password })) as AutopilotSession;

/** Fin de connexion par mot de passe ou Google : ticket + preuve du code de sécurité → session. */
export const finishLogin = async (ticket: string, emailProof: string) =>
    (await post('/autopilot/auth/login-finish', { ticket, email_proof: emailProof })) as AutopilotSession;

/** Première connexion : nom de l'entreprise saisi (comme à l'inscription du Classique) → session. */
export const completeCompany = async (ticket: string, companyName: string) =>
    (await post('/autopilot/auth/company', { ticket, company_name: companyName })) as AutopilotSession;

export const loginWithGoogle = async (accessToken: string, companyName = '') =>
    (await post('/autopilot/auth/google', { access_token: accessToken, company_name: companyName })) as AutopilotSession;

/** Prolonge la session ou change d'entreprise. */
export const switchSession = async (companyId = '') => {
    const token = localStorage.getItem(AUTOPILOT_TOKEN_KEY) || '';
    const s = await post('/autopilot/auth/session', { company_id: companyId }, token);
    if (s?.token) {
        try { localStorage.setItem(AUTOPILOT_TOKEN_KEY, s.token); } catch { /* stockage indisponible */ }
    }
    return s as AutopilotSession;
};

/** Entreprises, rôles et BU de la personne connectée. */
export const accountMe = async (): Promise<any> => {
    const token = localStorage.getItem(AUTOPILOT_TOKEN_KEY) || '';
    const { data } = await axios.get(`${apiUrl()}/autopilot/accounts/me`,
        { headers: { ...origin(), Authorization: `Bearer ${token}` }, timeout: 30000 });
    return data;
};
